import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, ShieldCheck, FlaskConical, Stethoscope, Truck } from "lucide-react";
import { getProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import IntakeMock from "@/components/IntakeMock";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/3f0d16b5-7625-490e-9a2f-404619fe3de7/images/a420627eb5f84162c055e69821fac267ee01917d83d3a2ae01adc78447b220d4.jpeg";
const SCIENCE_IMG = "https://images.pexels.com/photos/9146369/pexels-photo-9146369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const marquee = ["Physician-Reviewed", "Third-Party Tested", "Research-Grade Purity", "AI-Personalized Protocols", "Bio-Identical", "Powered by Claude AI"];

const steps = [
  { icon: Sparkles, title: "Take the AI assessment", body: "Chat with Vera, our AI concierge. She learns your goals, history, and lifestyle in under two minutes." },
  { icon: FlaskConical, title: "Get your protocol", body: "Receive a personalized peptide stack matched to your objectives, with clear guidance on each." },
  { icon: Stethoscope, title: "Physician review", body: "A licensed provider reviews every recommendation before anything ships to your door." },
  { icon: Truck, title: "Delivered discreetly", body: "Cold-chain shipped, research-grade peptides arrive in premium, discreet packaging." },
];

const testimonials = [
  { quote: "The AI intake understood exactly what I needed. My recovery peptide stack has been a game-changer after training.", name: "Marcus T.", role: "Endurance athlete" },
  { quote: "Finally a peptide company that feels considered and clinical, not like a shady forum. Sable is on another level.", name: "Elena R.", role: "Longevity enthusiast" },
];

const faqs = [
  { q: "What are peptides?", a: "Peptides are short chains of amino acids that act as signaling molecules in the body, directing processes like tissue repair, metabolism, and cellular regeneration." },
  { q: "Is a prescription required?", a: "Every order is reviewed by a licensed provider. Our AI assessment gathers what's needed, and our medical team ensures each protocol is appropriate for you." },
  { q: "How does the AI assessment work?", a: "Vera, powered by advanced AI, asks a few focused questions about your goals and history, then recommends the peptides best matched to your objectives." },
  { q: "How are the peptides tested?", a: "All products are third-party lab tested for purity and potency, and manufactured to research-grade standards." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    getProducts({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-bone">
      {/* HERO */}
      <section className="relative pt-[72px]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-8 items-center pt-14 md:pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-forest bg-sage px-4 py-2 rounded-full">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> AI-Personalized Peptide Therapy
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink mt-6">
              Elevate your <span className="italic">biology.</span>
            </h1>
            <p className="text-lg md:text-xl text-stone leading-relaxed mt-6 max-w-xl">
              Sable Health pairs research-grade peptides with an AI concierge that builds a protocol
              around your goals — reviewed by licensed physicians, delivered to your door.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <button
                data-testid="hero-intake-btn"
                onClick={() => navigate("/intake")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-[#14100F] px-7 py-3.5 text-base font-semibold hover:bg-forest-dark active:scale-95 transition-[background-color,transform] duration-200"
              >
                Take the assessment <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-transparent text-ink px-7 py-3.5 text-base font-semibold hover:bg-sand transition-colors duration-200"
              >
                Browse peptides
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-stone">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-forest" strokeWidth={1.5} /> Physician-reviewed</span>
              <span className="flex items-center gap-2"><FlaskConical className="w-4 h-4 text-forest" strokeWidth={1.5} /> Lab-tested purity</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-sand ring-1 ring-line">
              <img src={HERO_IMG} alt="Sable Health sculptural brand motif" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 md:left-6 bg-bone border border-line rounded-2xl shadow-xl px-5 py-4 max-w-[220px]">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone">Your protocol</p>
              <p className="font-display text-lg text-ink mt-1 leading-snug">Matched in under 2 minutes</p>
            </div>
          </motion.div>
        </div>

        {/* Trust marquee */}
        <div className="border-y border-line bg-sand/60 overflow-hidden py-4">
          <div className="flex gap-14 animate-marquee whitespace-nowrap w-max">
            {[...marquee, ...marquee].map((m, i) => (
              <span key={i} className="text-sm font-semibold text-stone flex items-center gap-14">
                {m} <span className="text-forest">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* AI INTAKE TEASER */}
      <section id="how" className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <IntakeMock />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-forest">The Sable Method</span>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-4 leading-[1.1]">
              Your personalized protocol awaits.
            </h2>
            <p className="text-lg text-stone leading-relaxed mt-5">
              Skip the guesswork and the endless forum research. Our AI concierge analyzes your
              goals and prescribes the optimal peptide stack — all reviewed by a licensed provider.
            </p>
            <div className="mt-8 space-y-5">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-11 h-11 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-5 h-5 text-forest" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{s.title}</p>
                    <p className="text-sm text-stone leading-relaxed mt-0.5">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              data-testid="teaser-intake-btn"
              onClick={() => navigate("/intake")}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-forest text-[#14100F] px-7 py-3.5 text-base font-semibold hover:bg-forest-dark active:scale-95 transition-[background-color,transform] duration-200"
            >
              Take the assessment <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-forest">The Collection</span>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-3 leading-[1.1]">
              Curated for peak performance.
            </h2>
          </div>
          <Link to="/shop" data-testid="view-all-link" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-forest hover:gap-3 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featured.slice(0, 6).map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* SCIENCE */}
      <section id="science" className="relative mt-20 md:mt-28">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[560px]">
            <img src={SCIENCE_IMG} alt="Scientist in laboratory" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-bone/10 to-bone/40 lg:to-bone/70" />
          </div>
          <div className="bg-sand flex items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="px-6 md:px-14 py-16 md:py-24 max-w-xl"
            >
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-forest">The Science</span>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-4 leading-[1.1]">
                Signaling molecules, precisely applied.
              </h2>
              <p className="text-lg text-stone leading-relaxed mt-5">
                Peptides are the body's own messengers — short amino acid chains that instruct cells
                to repair, regenerate, and optimize. We source research-grade compounds and pair them
                with clinical oversight so you get results without the guesswork.
              </p>
              <div className="grid grid-cols-3 gap-6 mt-10">
                {[["99%+", "Tested purity"], ["13+", "Targeted peptides"], ["100%", "Physician-reviewed"]].map(([n, l]) => (
                  <div key={l}>
                    <p className="font-display text-3xl md:text-4xl text-forest">{n}</p>
                    <p className="text-xs text-stone mt-1 leading-tight">{l}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 text-center">
        <div className="space-y-20">
          {testimonials.map((t, i) => (
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-display italic text-2xl md:text-4xl leading-snug text-ink">"{t.quote}"</p>
              <footer className="mt-6 text-sm text-stone">
                <span className="font-semibold text-ink">{t.name}</span> · {t.role}
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 md:px-8 pb-24">
        <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink text-center mb-12">
          Questions, answered.
        </h2>
        <div className="divide-y divide-line border-y border-line">
          {faqs.map((f, i) => (
            <div key={i} data-testid={`faq-${i}`} className="py-5">
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between text-left gap-4"
              >
                <span className="font-semibold text-lg text-ink">{f.q}</span>
                <span className={`text-forest text-2xl transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {openFaq === i && (
                <p className="text-stone leading-relaxed mt-3 pr-8">{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="rounded-[2rem] bg-sand border border-line text-ink px-8 md:px-16 py-16 md:py-24 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
              Ready to elevate?
            </h2>
            <p className="text-stone text-lg mt-5 max-w-xl mx-auto">
              Take the two-minute AI assessment and discover the peptide protocol built for your biology.
            </p>
            <button
              data-testid="cta-intake-btn"
              onClick={() => navigate("/intake")}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-forest text-[#14100F] px-8 py-4 text-base font-semibold hover:bg-forest-dark active:scale-95 transition-[background-color,transform] duration-200"
            >
              Start your assessment <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-forest/10 blur-3xl" />
          <div className="absolute -left-24 -bottom-24 w-80 h-80 rounded-full bg-taupe/5 blur-3xl" />
        </div>
      </section>
    </div>
  );
}
